import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useVideoRecorder } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimeclockComplianceVideoProof/hooks/useVideoRecorder";
import { useCallback, useEffect, useRef, useState } from "react";

import { useUploadReviewMedia } from "../api/useUploadReviewMedia";
import { VideoPreviewScreen } from "../components/VideoPreviewScreen";
import { VideoRecorderScreen } from "../components/VideoRecorderScreen";
import { type LeaveReviewEntrypoint, type StepAction } from "../types";

const MAX_VIDEO_DURATION_SECONDS = 120;

export interface VideoReviewStepProps {
  workplaceId: string;
  shiftId?: string;
  source: LeaveReviewEntrypoint;
  onAction: (action: StepAction) => void;
}

export function VideoReviewStep(props: VideoReviewStepProps) {
  const { workplaceId, shiftId, source, onAction } = props;
  const { showErrorToast } = useToast();

  const [videoBlob, setVideoBlob] = useState<Blob>();
  const [videoUrl, setVideoUrl] = useState<string>();
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");
  const lastRecordingTimeRef = useRef(0);

  const [videoElement, setVideoElement] = useState<HTMLVideoElement>();

  const handleVideoRecorded = useCallback((blob: Blob) => {
    setVideoBlob(blob);
    const url = URL.createObjectURL(blob);
    setVideoUrl(url);
  }, []);

  const {
    isReady,
    isRecording,
    recordingTime,
    isErrored,
    errorMessage,
    startRecording,
    stopRecording,
    initialise,
    destroy,
  } = useVideoRecorder({
    videoPreviewRef: videoElement,
    facingMode,
    maxRecordingTime: MAX_VIDEO_DURATION_SECONDS,
    onVideoRecorded: handleVideoRecorded,
  });

  const uploadMutation = useUploadReviewMedia();

  if (recordingTime !== undefined) {
    lastRecordingTimeRef.current = recordingTime;
  }

  useEffect(() => {
    if (videoElement) {
      initialise().catch(() => {
        // Error is handled by hook state (isErrored/errorMessage)
      });
    }
  }, [videoElement, initialise]);

  useEffect(() => {
    if (videoBlob) {
      destroy();
    }
  }, [videoBlob, destroy]);

  useEffect(() => {
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  const handleSetVideoRef = useCallback((element: HTMLVideoElement | null) => {
    setVideoElement(element ?? undefined);
  }, []);

  function handleFlipCamera() {
    destroy();
    setFacingMode((current) => (current === "user" ? "environment" : "user"));
  }

  function handleStartRecording() {
    logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_VIDEO_RECORDING_STARTED, {
      workplaceId,
      shiftId,
      source,
    });
    startRecording();
  }

  function handleReRecord() {
    logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_VIDEO_RE_RECORDED, {
      workplaceId,
      shiftId,
      source,
    });

    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
    }

    destroy();
    setVideoBlob(undefined);
    setVideoUrl(undefined);
    lastRecordingTimeRef.current = 0;
  }

  async function handleSubmit() {
    if (!videoBlob) {
      return;
    }

    try {
      const mimeType = videoBlob.type || "video/mp4";
      const result = await uploadMutation.mutateAsync({
        workplaceId,
        mediaType: "VIDEO",
        mimeType,
        fileBlob: videoBlob,
      });

      onAction({
        type: "NEXT_STEP",
        data: {
          mediaFileKey: result.fileKey,
          mediaMimeType: mimeType,
          mediaDurationSeconds: lastRecordingTimeRef.current,
          mediaFileSizeBytes: videoBlob.size,
        },
      });
    } catch {
      logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_VIDEO_UPLOAD_FAILED, {
        workplaceId,
        shiftId,
        source,
      });
      showErrorToast("Failed to upload video. Please try again.");
    }
  }

  if (videoUrl && videoBlob) {
    return (
      <VideoPreviewScreen
        durationSeconds={lastRecordingTimeRef.current}
        isUploading={uploadMutation.isLoading}
        videoUrl={videoUrl}
        workplaceId={workplaceId}
        onReRecord={() => handleReRecord()}
        onSubmit={() => handleSubmit()}
      />
    );
  }

  return (
    <VideoRecorderScreen
      errorMessage={errorMessage}
      isErrored={isErrored}
      isReady={isReady}
      isRecording={isRecording}
      recordingTime={recordingTime ?? 0}
      videoRef={handleSetVideoRef}
      onFlipCamera={() => handleFlipCamera()}
      onStartRecording={() => handleStartRecording()}
      onStopRecording={() => stopRecording()}
    />
  );
}
